import { Action } from '@reduxjs/toolkit';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { Dispatch } from 'redux';

import { useAnalytics } from '../analytics';
import { IdleTimerProviderMeldSkade } from '../components/idle-timer-provider/idle-timer-provider-meld-skade';
import { useBankregnr, useConfig } from '../config';
import { useKunde } from '../kundeinfo/kunde-context';
import { AppState } from '../reducers';
import { RemoteData, RemoteDataType } from '../reducers/remoteData';
import { RegisterSporsmalFullfortProvider } from '../registerSporsmalFullfortContext';
import { urler } from '../router/router-urler';
import { useBesvarteSporsmalFromCache, useWindowScrollTop } from '../sporsmalsflyt/hooks';
import { BesvartSporsmal } from '../sporsmalsflyt/reducer/current-steg';
import { previousStepAction } from '../sporsmalsflyt/reducer/sporsmalsflyt-actions';
import { isFirstSporsmal } from '../sporsmalsflyt/reducer/sporsmalsflyt-state';
import { Sporsmalsflyt } from '../sporsmalsflyt/sporsmalsflyt';
import { Skademeldingstype, SporsmalsflytResultDTO } from '../types/generated-types';
import { SideType } from '../utils/piwik-utils';
import BesvarteSporsmalModal from './besvarteSporsmal/besvarteSporsmal-modal';
import { redirectToLandingsside } from './redirect-to-landingsside';
import {
    fetchedSporsmalsflyt,
    fetchingSporsmalsflyt,
    fetchSporsmalsflytFailed,
    getSporsmalsflytApi,
} from './reducer/skademelding-actions';
import { userHasFilledInInfo } from './reducer/skademelding-reducer';
import { getSkademeldingstypeFromQueryParam, getSkadetypeNavn } from './reducer/skadetype';
import { SkademeldingOppsummering } from './skademelding-oppsummering';

export const Skademelding: FC = () => {
    const navigate = useNavigate();
    const dispatch: Dispatch<Action> = useDispatch();

    const location = useLocation();
    const skadetype = getSkademeldingstypeFromQueryParam(location.search);
    const skademeldingState = useSelector((state: AppState) => state.skademelding);
    const bankregnr = useBankregnr();
    const { links } = useConfig();
    const windowScrollTop = useWindowScrollTop();

    const { kundeinfo, getKundeinfo } = useKunde();
    const besvarteSporsmalFromCache: RemoteData<BesvartSporsmal[]> = useBesvarteSporsmalFromCache(
        kundeinfo?.kundenummer,
        bankregnr,
        skademeldingState.skadetype,
    );

    const { sporsmalsflytState } = skademeldingState;

    const [openBesvarteSporsmalModal, setOpenBesvarteSporsmalModal] = useState<boolean>(false);

    const onPrev = () => {
        if (isFirstSporsmal(sporsmalsflytState.besvarteSporsmal)) {
            redirectToLandingsside(skadetype, links, navigate, () => navigate(-1));
        }
        windowScrollTop();
        dispatch(previousStepAction());
    };

    const getSporsmalsflyt = useCallback(
        (skadetype: Skademeldingstype) => {
            dispatch(fetchingSporsmalsflyt(skadetype));
            getSporsmalsflytApi(skadetype, bankregnr)
                .then((result: SporsmalsflytResultDTO) =>
                    dispatch(
                        fetchedSporsmalsflyt(
                            result.sporsmalsflyt,
                            result.fremtindSkadeforsikringKonvertingStartDate,
                            result.enableRoutingToFremtind,
                            result.fremtindUrl,
                        ),
                    ),
                )
                .catch(() => dispatch(fetchSporsmalsflytFailed()));
        },
        [bankregnr, dispatch],
    );

    useAnalytics(skadetype, sporsmalsflytState.currentSteg);

    useEffect(() => {
        dispatch(fetchingSporsmalsflyt(skadetype));
        getKundeinfo(skadetype).finally(() => {
            getSporsmalsflyt(skadetype);
        });
    }, [dispatch, getKundeinfo, skadetype, bankregnr, getSporsmalsflyt]);

    useEffect(() => {
        if (besvarteSporsmalFromCache.type === RemoteDataType.SUCCESS && besvarteSporsmalFromCache.data.length > 1) {
            setOpenBesvarteSporsmalModal(true);
        }
    }, [besvarteSporsmalFromCache, besvarteSporsmalFromCache.type]);

    useEffect(() => {
        if (!skademeldingState.validSession) {
            navigate(urler().invalidSessionUrl);
        }
    });

    const header = {
        title: getSkadetypeNavn(skadetype),
        subtitle: 'Skademelding',
    };

    return (
        <RegisterSporsmalFullfortProvider skadetype={skadetype}>
            <IdleTimerProviderMeldSkade skademeldingstype={skadetype}>
                <BesvarteSporsmalModal
                    openModal={openBesvarteSporsmalModal}
                    setOpen={() => setOpenBesvarteSporsmalModal(true)}
                    besvarteSporsmal={besvarteSporsmalFromCache}
                    closeModal={() => setOpenBesvarteSporsmalModal(false)}
                />
                <Sporsmalsflyt
                    header={header}
                    currentSteg={skademeldingState.sporsmalsflytState.currentSteg}
                    userHasFilledInInfo={userHasFilledInInfo(skademeldingState)}
                    Oppsummering={SkademeldingOppsummering}
                    retryFetchingData={() => {
                        dispatch(fetchingSporsmalsflyt(skadetype));
                        getKundeinfo(skadetype).finally(() => {
                            getSporsmalsflyt(skadetype);
                        });
                    }}
                    onPrev={onPrev}
                    skjemaside={{ type: SideType.Skjemaside, skademeldingstype: skadetype }}
                />
            </IdleTimerProviderMeldSkade>
        </RegisterSporsmalFullfortProvider>
    );
};
